import { usePublicLineItemTranslations } from "@babackapp/private-api-client/client/use-public-line-item-translations";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  type LocalizableLineItem,
  localizeLineItem,
  localizeOptionName,
  localizeOptionValue,
  localizeProductTitle,
} from "../lib/line-item-translations.lib";
import { usePublicOrder } from "../queries/use-public-order";

type ExchangeCandidates = {
  variantByLineItemId?: Record<
    string,
    { products?: { productId?: string | number | null }[] }
  >;
};

/**
 * One request per language covers the order lines and the exchange candidates:
 * both hooks below share its cache entry.
 */
function useOrderTranslations() {
  // The URL `:shopId` param is the Shopify providerId (see useWidgetParams).
  const { shopId, orderId } = useParams<{ shopId: string; orderId: string }>();
  const { data: order } = usePublicOrder(orderId!);
  const { i18n } = useTranslation();
  const locale = i18n.language ?? "";

  const productIds = useMemo(() => {
    const ids = new Set<string>();
    for (const li of order?.lineItems ?? []) {
      if (li.product?.productId) ids.add(li.product.productId);
    }
    const candidates = (order as ExchangeCandidates | undefined)
      ?.variantByLineItemId;
    for (const entry of Object.values(candidates ?? {})) {
      for (const p of entry.products ?? []) {
        if (p.productId) ids.add(String(p.productId));
      }
    }
    return [...ids];
  }, [order]);

  const { data } = usePublicLineItemTranslations(
    shopId && locale ? { providerId: shopId, productIds, locale } : null,
  );

  return { locale, products: data?.products };
}

/**
 * Translates an order line's product name and variant options into the
 * portal's language, BAB-3184. The order's products are fetched once per
 * language; until they arrive, or when the shop has no published translation,
 * the line keeps what it has.
 *
 * Display only: matching (exchange variants, rules) must keep the raw values.
 */
export function useLocalizeLineItem() {
  const { locale, products } = useOrderTranslations();

  return useCallback(
    <T extends LocalizableLineItem>(item: T): T =>
      localizeLineItem(item, locale, products),
    [locale, products],
  );
}

/**
 * Display labels of the exchange picker, BAB-3379. Names and values keep their
 * raw form as matching keys: only what is rendered goes through here.
 */
export function useLocalizeProductOptions() {
  const { locale, products } = useOrderTranslations();

  return useMemo(
    () => ({
      optionName: (
        productId: string | number | null | undefined,
        name: string,
      ) => localizeOptionName(products, productId, name),
      optionValue: (
        productId: string | number | null | undefined,
        name: string,
        value: string,
      ) => localizeOptionValue(products, productId, name, value),
      title: (
        productId: string | number | null | undefined,
        fallback: string,
      ) => localizeProductTitle(products, productId, locale, fallback),
    }),
    [locale, products],
  );
}
